/**
 * Client-side logging utility for troubleshooting and debugging
 */

interface LogEvent {
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  category: string;
  event: string;
  data?: any;
  userAge?: number;
  conversationId?: string;
}

export class Logger {
  private static instance: Logger;
  private logs: LogEvent[] = [];
  private maxLogs = 1000;
  private fileLoggingEnabled = true;
  private logBuffer: LogEvent[] = [];
  private bufferFlushInterval = 5000; // Flush every 5 seconds
  private bufferSize = 50; // Flush when buffer reaches 50 entries

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private log(level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR', category: string, event: string, data?: any, userAge?: number, conversationId?: string): void {
    const logEvent: LogEvent = {
      timestamp: new Date().toISOString(),
      level,
      category,
      event,
      data,
      userAge,
      conversationId
    };

    this.logs.push(logEvent);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output for development
    if ((window as any).NODE_ENV === 'development' || !window.location.href.includes('://localhost')) {
      const message = `[${level}] ${category}:${event}`;
      switch (level) {
        case 'DEBUG':
          console.debug(message, data);
          break;
        case 'INFO':
          console.info(message, data);
          break;
        case 'WARN':
          console.warn(message, data);
          break;
        case 'ERROR':
          console.error(message, data);
          break;
      }
    }

    // Add to file logging buffer
    if (this.fileLoggingEnabled) {
      this.logBuffer.push(logEvent);
      
    }
  }

  

  debug(category: string, event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.log('DEBUG', category, event, data, userAge, conversationId);
  }

  info(category: string, event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.log('INFO', category, event, data, userAge, conversationId);
  }

  warn(category: string, event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.log('WARN', category, event, data, userAge, conversationId);
  }

  error(category: string, event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.log('ERROR', category, event, data, userAge, conversationId);
  }

  // Specialized logging methods for different categories
  logVoiceLiveEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('VOICE_LIVE', event, data, userAge, conversationId);
  }

  logAudioEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('AUDIO', event, data, userAge, conversationId);
  }

  logAudioEventDEBUG(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.debug('AUDIO', event, data, userAge, conversationId);
  }

  logUIEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('UI', event, data, userAge, conversationId);
  }

  logAgeAdaptationEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('AGE_ADAPTATION', event, data, userAge, conversationId);
  }

  logConnectionEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('CONNECTION', event, data, userAge, conversationId);
  }

  logTroubleshootingEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.warn('TROUBLESHOOTING', event, data, userAge, conversationId);
  }

  logErrorEvent(event: string, error: Error, data?: any, userAge?: number, conversationId?: string): void {
    this.error('ERROR', event, {
      message: error.message,
      stack: error.stack,
      ...data
    }, userAge, conversationId);
  }

  logStoryEvent(event: string, data?: any, userAge?: number, conversationId?: string): void {
    this.info('STORY', event, data, userAge, conversationId);
  }

  // Get logs for troubleshooting
  getLogs(category?: string, level?: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'): LogEvent[] {
    return this.logs.filter(log => 
      (!category || log.category === category) &&
      (!level || log.level === level)
    );
  }

  getRecentErrors(limit: number = 10): LogEvent[] {
    return this.logs
      .filter(log => log.level === 'ERROR')
      .slice(-limit);
  }

  // Export logs for support
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Clear logs
  clearLogs(): void {
    this.logs = [];
  }

  // Get system info for troubleshooting
  getSystemInfo(): any {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: window.screen?.width || 0,
        height: window.screen?.height || 0,
        colorDepth: window.screen?.colorDepth || 0
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      audioSupport: {
        audioContext: typeof window.AudioContext !== 'undefined',
        mediaRecorder: typeof window.MediaRecorder !== 'undefined',
        getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      },
      timestamp: new Date().toISOString()
    };
  }
}

// Export singleton instance
export const logger = Logger.getInstance();